import { useEffect, useState } from 'react';
import '../Employees.css';
import { env } from '../../../config/env';
import { logger } from '../../../shared/utils/logger.js';
import ModuleNotEnabledState from '../../../shared/components/ModuleNotEnabledState';
import EmployeesTab from './EmployeesTab';
import RolesTab from './RolesTab';

type EmployeesWorkspaceProps = {
  enabled?: boolean;
  onToast?: (toast: { variant?: 'success' | 'error' | 'info'; message: string }) => void;
};

type WorkspaceTab = 'roles' | 'employees';

const MODULE = 'employees-workspace';

const logEvent = (event: string, fields: Record<string, unknown> = {}): void => {
  logger.info(MODULE, event, { event, ...fields });
};

/**
 * C4/C5 — Employees workspace shell. Hosts the Roles tab (C4) and Employees
 * tab (C5). Schedule + assignment tabs will be added in C6 and C7.
 */
const EmployeesWorkspace = ({ onToast, enabled = env.servicesEmployeesRolesEnabled }: EmployeesWorkspaceProps) => {
  const featureEnabled = enabled;
  const [activeTab, setActiveTab] = useState<WorkspaceTab>('employees');

  const relayToast = (toast: { type: 'success' | 'error'; message: string }) => {
    onToast?.({
      message: toast.message,
      variant: toast.type === 'success' ? 'success' : 'error',
    });
  };

  useEffect(() => {
    logEvent('workspace_mounted', { featureEnabled });
  }, [featureEnabled]);

  if (!featureEnabled) {
    return (
      <ModuleNotEnabledState
        title="Employees workspace is disabled"
        description="Enable the staffing module for this store and keep the deployment rollout flag on to access role and employee management."
      />
    );
  }

  return (
    <section className="employees-workspace" aria-live="polite">
      <header className="employees-workspace__header">
        <div>
          <h1 className="employees-workspace__title">Employees</h1>
          <p className="employees-workspace__subtitle">
            Manage staff roles, profiles, schedules, and service assignments.
          </p>
        </div>
      </header>

      <div className="employees-workspace__tabs" role="tablist" aria-label="Employees sections">
        <button
          type="button"
          role="tab"
          className="employees-workspace__tab"
          aria-selected={activeTab === 'employees'}
          onClick={() => {
            logEvent('workspace_tab_change', { next: 'employees' });
            setActiveTab('employees');
          }}
        >
          Employees
        </button>
        <button
          type="button"
          role="tab"
          className="employees-workspace__tab"
          aria-selected={activeTab === 'roles'}
          onClick={() => {
            logEvent('workspace_tab_change', { next: 'roles' });
            setActiveTab('roles');
          }}
        >
          Roles
        </button>
      </div>

      <RolesTab active={activeTab === 'roles'} onToast={relayToast} />
      <EmployeesTab active={activeTab === 'employees'} onToast={relayToast} />
    </section>
  );
};

export default EmployeesWorkspace;
